// @ts-strict-ignore
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Ellipsis, FileText } from 'lucide-react';

import * as monthUtils from 'loot-core/src/shared/months';
import { useSyncedPref } from '../../../../hooks/useSyncedPref';

import { useUndo } from '../../../../hooks/useUndo';
import { Button } from '../../../ui/button2';
import { Popover } from '../../../ui/popover2';
import { Card, CardContent, CardFooter } from '../../../ui/card';
import { View } from '../../../common/View';
import { NotesButton } from '../../../NotesButton';
import { NamespaceContext } from '../../../spreadsheet/NamespaceContext';
import { useTrackingBudget } from '../TrackingBudgetContext';

import { BudgetMonthMenu } from './BudgetMonthMenu';
import { ExpenseTotal } from './ExpenseTotal';
import { IncomeTotal } from './IncomeTotal';
import { Saved } from './Saved';

type BudgetSummaryProps = {
  month?: string;
};
export function BudgetSummary({ month }: BudgetSummaryProps) {
  const { t } = useTranslation();
  const {
    currentMonth,
    summaryCollapsed: collapsed,
    onBudgetAction,
    onToggleSummaryCollapse,
    hideIncome,
  } = useTrackingBudget();

  const [menuOpen, setMenuOpen] = useState(false);
  const triggerRef = useRef(null);
  const { showUndoNotification } = useUndo();

  function onMenuOpen() {
    setMenuOpen(true);
  }

  function onMenuClose() {
    setMenuOpen(false);
  }

  const displayMonth = monthUtils.format(month, "MMMM 'yy");
  const [_currencyFilter] = useSyncedPref('budgetCurrencyFilter');
  const currencyCode = (_currencyFilter as string) || 'PEN';

  return (
    <NamespaceContext.Provider value={monthUtils.sheetForMonth(month)}>
      <Card
        data-testid="budget-summary"
        className={
          month === currentMonth
            ? 'mt-[5px] mb-[5px] border-primary/30 shadow-md py-3 gap-2'
            : 'mt-[5px] mb-[5px] shadow-sm py-3 gap-2'
        }
        style={{ cursor: 'default', flex: 1 }}
      >
        {/* Three-zone header: spacer | centered title | actions */}
        <div className="flex items-center border-b pb-2 px-4">
          <div className="w-[52px]" />
          <div className="flex-1 text-center text-base font-semibold">
            {monthUtils.format(month, 'MMMM')}
          </div>
          <div className="w-[52px] flex flex-row items-center justify-end gap-0.5">
            <NotesButton
              id={`budget-${month}`}
              width={15}
              height={15}
              tooltipPosition="bottom right"
              defaultColor={'var(--color-pageTextLight)'}
            />
            <Button
              ref={triggerRef}
              variant="bare"
              aria-label={t('Menu')}
              onClick={onMenuOpen}
            >
              <Ellipsis size={15} className="text-muted-foreground" />
            </Button>
            <Popover
              triggerRef={triggerRef}
              isOpen={menuOpen}
              onOpenChange={onMenuClose}
            >
              <BudgetMonthMenu
                onCopyLastMonthBudget={() => {
                  onBudgetAction(month, 'copy-last');
                  onMenuClose();
                  showUndoNotification({
                    message: t(
                      '{{displayMonth}} budgets have all been set to last month\u2019s budgeted amounts.',
                      { displayMonth },
                    ),
                  });
                }}
                onSetBudgetsToZero={() => {
                  onBudgetAction(month, 'set-zero');
                  onMenuClose();
                  showUndoNotification({
                    message: t(
                      '{{displayMonth}} budgets have all been set to zero.',
                      { displayMonth },
                    ),
                  });
                }}
                onSetMonthsAverage={numberOfMonths => {
                  onBudgetAction(month, `set-${numberOfMonths}-avg`);
                  onMenuClose();
                  showUndoNotification({
                    message:
                      numberOfMonths === 12
                        ? t(
                            `${displayMonth} budgets have all been set to yearly average.`,
                          )
                        : t(
                            `${displayMonth} budgets have all been set to ${numberOfMonths} month average.`,
                          ),
                  });
                }}
                onCheckTemplates={() => {
                  onBudgetAction(month, 'check-templates');
                  onMenuClose();
                }}
                onApplyBudgetTemplates={() => {
                  onBudgetAction(month, 'apply-goal-template');
                  onMenuClose();
                  showUndoNotification({
                    message: t(
                      '{{displayMonth}} budget templates have been applied.',
                      { displayMonth },
                    ),
                  });
                }}
                onOverwriteWithBudgetTemplates={() => {
                  onBudgetAction(month, 'overwrite-goal-template');
                  onMenuClose();
                  showUndoNotification({
                    message: t(
                      '{{displayMonth}} budget templates have been overwritten.',
                      { displayMonth },
                    ),
                  });
                }}
              />
            </Popover>
          </div>
        </div>

        {!collapsed && (
          <CardContent className="flex flex-col gap-2 pt-3 pb-0">
            {!hideIncome && <IncomeTotal />}
            <ExpenseTotal />
          </CardContent>
        )}

        <CardFooter className="border-t mt-1 pt-2 pb-2 flex flex-col items-stretch">
          <Saved projected={month >= currentMonth} currencyCode={currencyCode} />
        </CardFooter>
      </Card>
    </NamespaceContext.Provider>
  );
}
